import React, { useRef, useState } from "react";
import { HomeOutlined, UserOutlined, SearchOutlined } from "@ant-design/icons";
import { Breadcrumb, Form, Input, Select, Table, Button, Space } from "antd";
import type { ColumnType } from "antd/es/table";
import type { InputRef } from "antd";
import "./data_invoice_work_order.css";
import useSWR from "swr";
import axios from "axios";
import { Link, useNavigate } from "react-router-dom";
import { STATUS_MAP } from "../../../components/hoc_work_order_invoice";

interface DataType {
    key: React.Key;
    status: string;
    invoice_no: string;
    trans_id: string;
    invoice_date: string;
    work_order_no: string;
    customer: string;
    chassis_no: string;
    warehouse: string;
    brand: string;
    npwp: string;
}

interface WarehouseType { warehouse_code: string; warehouse_name: string; }

const DataInvoiceWorkOrder: React.FC = () => {
    const fetcher = (url: string) => axios.get(url).then((res) => res.data);
    const navigate = useNavigate();
    const [searchText, setSearchText] = useState('');
    const [searchedColumn, setSearchedColumn] = useState('');
    const searchInput = useRef<InputRef>(null);
    const [isNavigating, setIsNavigating] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);

    const { data: invoiceData, isLoading } = useSWR<DataType[]>(
        `${process.env.REACT_APP_BACKEND_URL}/api/work-order-invoice/list-invoice`,
        fetcher
    );

    const sortedInvoiceData = React.useMemo(() => {
        if (!invoiceData) return [];
        return [...invoiceData].sort((a, b) => (b.invoice_no || "").localeCompare(a.invoice_no || "", undefined, { numeric: true }));
    }, [invoiceData]);

    const { data: warehouseData } = useSWR<WarehouseType[]>(
        `${process.env.REACT_APP_BACKEND_URL}/api/warehouse/list-data`,
        fetcher
    );

    const warehouseMap = React.useMemo(() => {
        if (!warehouseData) return new Map<string, string>();
        return new Map(warehouseData.map(item => [item.warehouse_code, item.warehouse_name]));
    }, [warehouseData]);

    const transformStyle = {
        transform: "scale(0.80)",
        transformOrigin: "top left",
        width: "125%",
    };


    const handleSearch = (selectedKeys: string[], confirm: () => void, dataIndex: keyof DataType) => {
        confirm();
        setSearchText(selectedKeys[0]);
        setSearchedColumn(dataIndex);
    };


    const handleReset = (clearFilters: () => void) => {
        clearFilters();
        setSearchText('');
    };


    const getColumnSearchProps = (dataIndex: keyof DataType): ColumnType<DataType> => ({
        filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters, close }) => (
            <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
                <Input
                    ref={searchInput}
                    placeholder={`Search ${dataIndex}`}
                    value={selectedKeys[0]}
                    onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
                    onPressEnter={() => handleSearch(selectedKeys as string[], confirm, dataIndex)}
                    style={{ marginBottom: 8, display: 'block' }}
                />
                <Space>
                    <Button
                        type="primary"
                        onClick={() => handleSearch(selectedKeys as string[], confirm, dataIndex)}
                        icon={<SearchOutlined />}
                        size="small"
                        style={{ width: 90 }}
                    >
                        Search
                    </Button>
                    <Button
                        onClick={() => clearFilters && handleReset(clearFilters)}
                        size="small"
                        style={{ width: 90 }}
                    >
                        Reset
                    </Button>
                </Space>
            </div>
        ),
        filterIcon: (filtered: boolean) => (
            <SearchOutlined style={{ color: filtered ? '#1677ff' : undefined }} />
        ),
        onFilter: (value, record) =>
            record[dataIndex]
                .toString()
                .toLowerCase()
                .includes((value as string).toLowerCase()),
        onFilterDropdownOpenChange: (visible) => {
            if (visible) {
                setTimeout(() => searchInput.current?.select(), 100);
            }
        },
    });

    const columns: ColumnType<DataType>[] = [
        {
            title: "Status",
            dataIndex: "status",
            key: "status",
            sorter: (a, b) => a.status.localeCompare(b.status),
            filters: Object.entries(STATUS_MAP).map(([value, text]) => ({ text, value })),
            onFilter: (value, record) => record.status === value,
            render: (value: string) => STATUS_MAP[value] || value
        },
        {
            title: "Invoice No",
            dataIndex: "invoice_no",
            key: "invoice_no",
            sorter: (a, b) => a.invoice_no.localeCompare(b.invoice_no),
            ...getColumnSearchProps("invoice_no"),
            render: (text: string, record: DataType) => (
                <Link
                    to={`/dashboard/work_order_invoice?transId=${record.trans_id}`}
                    state={{
                        workOrderNo: record.work_order_no,
                        invoiceStatus: record.status,
                        transId: record.trans_id,
                        brand: record.brand,
                        npwp: record.npwp
                    }}
                    onClick={(e) => e.stopPropagation()}
                >
                    {text}
                </Link>
            )
        },
        { title: "Invoice Date", dataIndex: "invoice_date", key: "invoice_date", sorter: (a, b) => a.invoice_date.localeCompare(b.invoice_date), ...getColumnSearchProps("invoice_date") },
        { title: "Work Order No", dataIndex: "work_order_no", key: "work_order_no", sorter: (a, b) => a.work_order_no.localeCompare(b.work_order_no), ...getColumnSearchProps("work_order_no") },
        { title: "Customer", dataIndex: "customer", key: "customer", sorter: (a, b) => a.customer.localeCompare(b.customer), ...getColumnSearchProps("customer") },
        { title: "Chassis No", dataIndex: "chassis_no", key: "chassis_no", sorter: (a, b) => a.chassis_no.localeCompare(b.chassis_no), ...getColumnSearchProps("chassis_no") },
        { title: "Warehouse", dataIndex: "warehouse", key: "warehouse", sorter: (a, b) => a.warehouse.localeCompare(b.warehouse), ...getColumnSearchProps("warehouse"), render: (value) => warehouseMap.get(value) || value },
    ];

    return (
        <div id="i3b9" style={transformStyle}>
            <div className="gjs-grid-row" id="i8o9">
                <div className="gjs-grid-column" id="iqd2">
                    <Breadcrumb
                        items={[
                            {
                                title: <HomeOutlined />,
                            },
                            {
                                title: (
                                    <>
                                        <span>Data Work Order Invoice</span>
                                    </>
                                ),
                            },
                            {
                                href: "#/dashboard/work_order_invoice",
                                title: "Work Order Invoice",
                            },
                        ]}
                    />
                </div>
            </div>
            <div className="gjs-grid-row" id="ih2ri">
                <div className="gjs-grid-column" id="iatk">
                    <Table
                        columns={columns}
                        dataSource={sortedInvoiceData}
                        loading={isLoading || isNavigating}
                        pagination={{
                            current: currentPage,
                            pageSize: pageSize,
                            showSizeChanger: true,
                            hideOnSinglePage: true,
                            pageSizeOptions: ["10", "20", "50", "100"],
                            onChange: (page, size) => {
                                setCurrentPage(page);
                                setPageSize(size);
                            },
                            showTotal: (total, range) => `${range[0]}-${range[1]} dari ${total} data`,
                        }}
                        rowKey="trans_id"
                        onRow={(record) => ({
                            onDoubleClick: () => {
                                if (isNavigating) return;
                                setIsNavigating(true);
                                navigate("/dashboard/work_order_invoice", { state: { workOrderNo: record.work_order_no, invoiceStatus: record.status, transId: record.trans_id, brand: record.brand, npwp: record.npwp } });
                            },
                        })}
                    />
                </div>
            </div>
        </div>
    );
};

export default DataInvoiceWorkOrder;